import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  announcementIcon,
  lucidEmailWarning,
  lucidMessageSquareIcon,
  lucidMessageWarning,
  lucidPhoneWarning,
  lucidStarIcon,
  lucidUserIcon,
  securityIcon,
  taskIcon,
} from '@g99-product-web-ui/shared-util-lib';

import {
  NotificationEnumType,
  NotificationReadStatusEnum,
} from '../../../../enums/notification.enum';
import { INotificationDto } from '../../../../interfaces/notification.interface';
import { timeAgo } from '../../../../utils/notification.utils';

@Component({
  selector: 'g99-lib-notification-card',
  templateUrl: './notification-card.component.html',
  styleUrl: './notification-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NotificationCardComponent {
  // Public properties (Inputs/Outputs first)
  @Input() public notification!: INotificationDto;
  @Output() public notificationClick = new EventEmitter<INotificationDto>();

  // Protected properties (used in template)
  protected readonly notificationIconMap = {
    [NotificationEnumType.TASK_ASSIGNED]: taskIcon,
    [NotificationEnumType.CAMPAIGN_FAILED]: announcementIcon,
    [NotificationEnumType.INBOX_CONVERSATION_ASSIGNED]: lucidMessageSquareIcon,
    [NotificationEnumType.LEAD_GENERATED]: lucidUserIcon,
    [NotificationEnumType.SECURITY]: securityIcon,
    [NotificationEnumType.CONTACT_REPLIES_TO_CONVERSATION]:
      lucidMessageSquareIcon,
    [NotificationEnumType.NEW_FEEDBACK_RECEIVED]: lucidStarIcon,
    [NotificationEnumType.EMAIL_QUOTA_BELOW_THRESHOLD]: lucidEmailWarning,
    [NotificationEnumType.SMS_QUOTA_BELOW_THRESHOLD]: lucidMessageWarning,
    [NotificationEnumType.VOICE_QUOTA_BELOW_THRESHOLD]: lucidPhoneWarning,
  };

  protected readonly NotificationReadStatusEnum = NotificationReadStatusEnum;

  // Protected methods (used in template)
  /**
   * @purpose Handles click event on notification card and emits the notification data
   * @param notification - The notification object that was clicked
   * @returns void
   */
  protected onClick(notification: INotificationDto): void {
    this.notificationClick.emit(notification);
  }

  /**
   * @purpose Gets the appropriate icon for the notification type based on the notification type mapping
   * @param notification - The notification object containing the type
   * @returns SVG icon string from the notification icon map
   */
  protected getNotificationIcon(notification: INotificationDto): string {
    return this.notificationIconMap[notification.type] || announcementIcon;
  }

  /**
   * @purpose Calculates and formats the time elapsed since the notification was created
   * @param date - The date when the notification was created (Date object or string)
   * @returns Formatted time ago string (e.g., "2 hours ago", "1 day ago")
   */
  protected calculateTimeAgo(date: Date | string): string {
    return timeAgo(date);
  }
}
